<script setup>
import { computed, onMounted, ref } from 'vue'
import { api } from '../api/client'
import {
  entryBooking as getEntryBooking,
  entryExternallyOccupied as getEntryExternallyOccupied,
  entryOccupied as getEntryOccupied,
} from '../api/dashboard'
import { normalizeDashboard, resourceTypeLabels } from '../api/mappers'
import { APP_TIMEZONE, formatDateTime, formatTime, toDateInput } from '../utils'
import AppIcon from '../components/AppIcon.vue'
import EmptyState from '../components/EmptyState.vue'
import LoadingState from '../components/LoadingState.vue'
import StatusBadge from '../components/StatusBadge.vue'
import { useToast } from '../composables/useToast'
import { useSession } from '../composables/useSession'
import { canManageCatalog } from '../api/users'

const selectedDate = ref(toDateInput())
const dashboard = ref(normalizeDashboard())
const loading = ref(true)
const { show } = useToast()
const session = useSession()
const canManage = computed(() => canManageCatalog(session.user))

const headingDate = computed(() => {
  const value = new Date(`${selectedDate.value}T12:00:00`)
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: APP_TIMEZONE,
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  }).format(value)
})
const isToday = computed(() => selectedDate.value === toDateInput())

function entryBooking(entry) {
  return getEntryBooking(entry, isToday.value)
}

function entryExternallyOccupied(entry) {
  return getEntryExternallyOccupied(entry, isToday.value)
}

function entryOccupied(entry) {
  return getEntryOccupied(entry, isToday.value)
}

const occupiedCount = computed(() => dashboard.value.resourceLoad.filter(entryOccupied).length)
const displayedOccupied = computed(() => (
  isToday.value ? dashboard.value.summary.occupiedNow : occupiedCount.value
))
const displayedOccupancyPercent = computed(() => {
  if (isToday.value) return dashboard.value.summary.occupancyPercent
  if (!dashboard.value.summary.totalResources) return 0
  return (displayedOccupied.value / dashboard.value.summary.totalResources) * 100
})

function calendarPart(value, part) {
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: APP_TIMEZONE,
    [part]: part === 'month' ? 'short' : 'numeric',
  }).format(new Date(value)).replace('.', '')
}

async function load() {
  loading.value = true
  try {
    dashboard.value = normalizeDashboard(await api.dashboard.get(selectedDate.value))
  } catch (error) {
    show(error.message || 'Не удалось загрузить сводку.', 'error')
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="page dashboard-page">
    <header class="page-header">
      <div>
        <span class="eyebrow">Оперативная сводка</span>
        <h1>Обзор базы</h1>
        <p class="page-subtitle">{{ headingDate }}</p>
      </div>
      <div class="page-actions">
        <label class="compact-field">
          <span class="sr-only">Дата сводки</span>
          <input v-model="selectedDate" type="date" @change="load" />
        </label>
        <button type="button" class="icon-button icon-button--bordered" aria-label="Обновить сводку" title="Обновить" @click="load">
          <AppIcon name="refresh" :size="18" />
        </button>
        <RouterLink to="/bookings?new=1" class="button button--primary">
          <AppIcon name="plus" :size="18" />
          Новая бронь
        </RouterLink>
      </div>
    </header>

    <LoadingState v-if="loading" />

    <template v-else>
      <section class="occupancy-overview" aria-labelledby="occupancy-title">
        <div class="occupancy-main">
          <span class="eyebrow">{{ isToday ? 'Сейчас' : 'На дату' }}</span>
          <div class="occupancy-value">
            <strong>{{ Math.round(displayedOccupancyPercent) }}<small>%</small></strong>
            <span>{{ isToday ? 'текущая' : 'плановая' }}<br />загрузка</span>
          </div>
          <div class="occupancy-track" aria-hidden="true">
            <span :style="{ width: `${Math.min(100, displayedOccupancyPercent)}%` }" />
          </div>
          <p id="occupancy-title">
            Занято {{ displayedOccupied }} из {{ dashboard.summary.totalResources }} активных объектов
          </p>
        </div>

        <dl class="metric-strip">
          <div>
            <dt>Объектов</dt>
            <dd>{{ dashboard.summary.totalResources }}</dd>
            <span>доступно для работы</span>
          </div>
          <div>
            <dt>Заезды</dt>
            <dd>{{ dashboard.summary.arrivalsToday }}</dd>
            <span>на выбранную дату</span>
          </div>
          <div>
            <dt>Выезды</dt>
            <dd>{{ dashboard.summary.departuresToday }}</dd>
            <span>на выбранную дату</span>
          </div>
        </dl>
      </section>

      <div class="dashboard-columns">
        <section class="content-section" aria-labelledby="load-heading">
          <header class="section-header">
            <div>
              <h2 id="load-heading">Загрузка объектов</h2>
              <p>Состояние на выбранную дату</p>
            </div>
            <RouterLink to="/calendar" class="text-link">Календарь <AppIcon name="arrow-right" :size="16" /></RouterLink>
          </header>

          <div v-if="dashboard.resourceLoad.length" class="resource-load-list">
            <article v-for="entry in dashboard.resourceLoad" :key="entry.resource.id" class="load-row">
              <span class="resource-symbol" :style="entry.resource.color ? { '--resource-color': entry.resource.color } : {}" aria-hidden="true">
                <AppIcon name="resources" :size="19" />
              </span>
              <div class="load-row__resource">
                <strong>{{ entry.resource.name }}</strong>
                <span>{{ resourceTypeLabels[entry.resource.resourceType] }}</span>
              </div>
              <div v-if="entryBooking(entry)" class="load-row__booking">
                <span>{{ entryBooking(entry).guestName }}</span>
                <small>{{ formatTime(entryBooking(entry).startsAt) }}–{{ formatTime(entryBooking(entry).endsAt) }}</small>
              </div>
              <div v-else-if="entryExternallyOccupied(entry)" class="load-row__booking load-row__booking--external">
                <span>Занято в Авито</span>
                <small>Внешний календарь</small>
              </div>
              <span class="availability" :class="entryOccupied(entry) ? 'availability--occupied' : 'availability--free'">
                {{ entryOccupied(entry) ? 'Занят' : 'Свободен' }}
              </span>
            </article>
          </div>
          <EmptyState v-else title="Объекты ещё не добавлены" :text="canManage ? 'Создайте первый дом, баню или зал в разделе объектов.' : 'Обратитесь к администратору для добавления объектов.'">
            <RouterLink v-if="canManage" to="/resources?new=1" class="button button--secondary">Добавить объект</RouterLink>
          </EmptyState>
        </section>

        <section class="content-section content-section--upcoming" aria-labelledby="upcoming-heading">
          <header class="section-header">
            <div>
              <h2 id="upcoming-heading">Ближайшие заезды</h2>
              <p>Следующие активные бронирования</p>
            </div>
            <RouterLink to="/bookings" class="text-link">Все брони <AppIcon name="arrow-right" :size="16" /></RouterLink>
          </header>

          <ol v-if="dashboard.upcoming.length" class="upcoming-list">
            <li v-for="booking in dashboard.upcoming" :key="booking.id" class="upcoming-item">
              <div class="upcoming-date">
                <strong>{{ calendarPart(booking.startsAt, 'day') }}</strong>
                <span>{{ calendarPart(booking.startsAt, 'month') }}</span>
              </div>
              <div class="upcoming-copy">
                <strong>{{ booking.guestName }}</strong>
                <span>{{ booking.resource.name }} · {{ booking.guestsCount }} чел.</span>
                <StatusBadge :status="booking.status" />
              </div>
            </li>
          </ol>
          <EmptyState v-else title="Ближайших заездов нет" text="На выбранный период активные бронирования не найдены." />
        </section>
      </div>
    </template>
  </div>
</template>
